import {
  BulkActionContainerCol,
  Col,
  DesignTokens,
  Table,
  TableActionBarRow,
  ValueOrFallback,
} from "melco-ui";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  BulkActionCount,
  FormattedDate,
  SharedserviceMelcoFusionModelsUserBlankListItem,
  Search,
  useTableSelection,
  TableCellLink,
  ViewContext,
} from "melco-shared-logic";
import { PublishedState } from "../../state/PublishedState";
import { useBlanks } from "../../../hooks/libraries/blanks/useBlanks";
import { BulkActionDeleteBlanks } from "./BulkActionDeleteBlanks";
import { BlanksListThumbnail } from "./BlanksListThumbnail";
import { TFunction } from "i18next";

const { spacings } = DesignTokens;

const DETAIL_VIEW_PATH = "/libraries/blanks";

export const blanksListColumns = (
  t: TFunction<"ns1">,
  context: ViewContext = ViewContext.List
) => {
  return [
    {
      title: "",
      key: "thumbnail",
      dataIndex: "thumbnail",
      render: (_: string, item: SharedserviceMelcoFusionModelsUserBlankListItem) => (
        <BlanksListThumbnail blank={item} />
      ),
      width: spacings.xlarge + 2 * spacings.middle, // 2 * spacings.middle to compensate for table padding
      height: spacings.xlarge + 2 * spacings.middle, // 2 * spacings.middle to compensate for table padding
    },
    {
      title: t("blanks_list.columns.name"),
      key: "name",
      dataIndex: "name",
      sorter: true,
      render: (_: string, item: SharedserviceMelcoFusionModelsUserBlankListItem) => (
        <ValueOrFallback
          value={item.name}
          render={(value) =>
            context === ViewContext.List ? (
              <TableCellLink to={[DETAIL_VIEW_PATH, item.id].join("/")}>
                {value}
              </TableCellLink>
            ) : (
              <>{value}</>
            )
          }
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("blanks_list.columns.views"),
      key: "views_count",
      dataIndex: "views_count",
      sorter: true,
      render: (_: string, item: SharedserviceMelcoFusionModelsUserBlankListItem) => (
        <ValueOrFallback
          value={item.views_count}
          useFallback={item.views_count === 0}
          fallback={t("global.fallback.none")}
        />
      ),
    },
    {
      title: t("blanks_list.columns.colors"),
      key: "colors_count",
      dataIndex: "colors_count",
      sorter: true,
      render: (_: string, item: SharedserviceMelcoFusionModelsUserBlankListItem) => (
        <ValueOrFallback
          value={item.colors_count}
          useFallback={item.colors_count === 0}
          fallback={t("global.fallback.none")}
        />
      ),
    },
    {
      title: t("blanks_list.columns.created_date"),
      key: "created_date",
      dataIndex: "created_date",
      sorter: true,
      render: (_: string, item: SharedserviceMelcoFusionModelsUserBlankListItem) => (
        <FormattedDate date={item.created_date} />
      ),
    },
    {
      title: t("blanks_list.columns.updated_date"),
      key: "updated_date",
      dataIndex: "updated_date",
      sorter: true,
      render: (_: string, item: SharedserviceMelcoFusionModelsUserBlankListItem) => (
        <FormattedDate date={item.updated_date} />
      ),
    },
  ];
};

export const BlanksList: React.FC = () => {
  const {
    isLoading,
    isFetching,
    data,
    isPreviousData,
    errorComponent,
    onTableConfigChange,
    pagination,
    searchQueryChange,
    refetch,
  } = useBlanks();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const { selectedRowKeys, setSelectedRowKeys, rowSelection, onRow } =
    useTableSelection((row: SharedserviceMelcoFusionModelsUserBlankListItem) => {
      navigate([DETAIL_VIEW_PATH, row.id].join("/"));
    });

  const columns = [
    ...blanksListColumns(t),
    {
      title: t("blanks_list.columns.publish_state"),
      key: "publish_state",
      dataIndex: "publish_state",
      sorter: true,
      render: (_: string, item: SharedserviceMelcoFusionModelsUserBlankListItem) => {
        return <PublishedState publishedState={item.publish_state ?? 1} />;
      },
    },
  ];

  return (
    <>
      <TableActionBarRow>
        <Col>
          <Search
            onSearchInputChange={(query: string) => searchQueryChange(query)}
          />
        </Col>
        <BulkActionContainerCol>
          <BulkActionCount selectedCount={selectedRowKeys.length} />
          <BulkActionDeleteBlanks
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            refetch={refetch}
            data={data}
          />
        </BulkActionContainerCol>
      </TableActionBarRow>
      <Table
        showSorterTooltip={false}
        sticky
        loading={isPreviousData ? isFetching : isLoading}
        dataSource={data}
        columns={columns}
        onChange={onTableConfigChange}
        pagination={pagination}
        rowKey="id"
        rowSelection={rowSelection}
        onRow={onRow}
        rowClassName={() => "clickable"}
        noDataMessage={t("blanks_list.empty")}
        errorComponent={errorComponent}
      />
    </>
  );
};
